// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  LinearWizard,
  Step,
} from "@components";
import {
  SelectEmployeeStep,
  SelectPositionToAssignShiftPatternToStep,
  TransferAbsenceConfirmationModal,
} from "@modules/shared/components";
import type { EmployeeType, RoleSummaryType } from "@modules/employee/types";
import type { State } from "@store/reducers";
import {
  getSelectedEmployee,
  getSelectedEmployeePosition,
  getTransferAbsenceConfirmationModal,
} from "@modules/shared/selectors";
import sharedActionCreator from "@modules/shared/actionCreator";
import type {
  EmployeeAbsence,
  TransferAbsenceConfirmationModalType,
} from "@modules/shared/reducer/employee-absences/types";

type PropsFromDispatch = $Exact<{
  submitTransferAbsence: typeof sharedActionCreator.SubmitTransferAbsence,
  selectEmployeePosition: typeof sharedActionCreator.SelectEmployeePosition,
  selectEmployee: typeof sharedActionCreator.SelectEmployee,
  toggleTransferAbsenceConfirmationModal: typeof sharedActionCreator.ToggleTransferAbsenceConfirmationModal,
  resetSelectedEmployees: typeof sharedActionCreator.ResetSelectedEmployees,
}>;

type PropsFromState = $Exact<{
  selectedEmployeePosition: RoleSummaryType | null,
  selectedEmployee: EmployeeType | null,
  transferAbsenceConfirmationModal: TransferAbsenceConfirmationModalType,
}>;

type Props = {
  employee: EmployeeType,
  clientId: number,
  absence: EmployeeAbsence,
  onDismissHandler: () => void,
  onSubmit: (form: Object) => void,
  onChange: (form: Object) => void,
  ...PropsFromLocalize,
  ...PropsFromDispatch,
  ...PropsFromState,
};

export class TransferAbsenceModal extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    // Class components needs constructor for accessing the SchemaForm's ref
    this.formRef = React.createRef();
  }
  validate: *;

  componentWillUnmount() {
    this.props.resetSelectedEmployees();
  }

  submitForm = () => {
    this.props.toggleTransferAbsenceConfirmationModal();
  };
  toggleEdit = (stepIndex?: number) => {
    if (stepIndex === 0) {
      this.props.selectEmployee(null);
    } else {
      this.props.selectEmployeePosition(null);
    }
  };

  isValid() {
    return this.props.selectedEmployee && this.props.selectedEmployeePosition;
  }

  toggleAbsenceConfirmationModal = () => {
    this.props.toggleTransferAbsenceConfirmationModal();
  };
  submitTransfer = () => {
    this.props.submitTransferAbsence({
      clientId: this.props.clientId,
      employeeId: this.props.employee.id,
      absenceId: this.props.absence.id,
      employeeToId: (this.props.selectedEmployee || {}).id,
      employeePositionToId: (this.props.selectedEmployeePosition || {}).id,
    });
  };

  render() {
    const selectedEmployee = this.props.selectedEmployee;
    return (
      <Modal className="min-modal-width">
        <ModalHeader toggle={this.props.onDismissHandler}>
          {this.props.t("TRANSFER_ABSENCE")} #{this.props.absence.id}
        </ModalHeader>
        <ModalBody>
          <LinearWizard data={[]} onToggleEdit={this.toggleEdit}>
            <Step
              title={this.props.t("SELECT_EMPLOYEE_TO_TRANSFER_ABSENCE_TO")}
              clientId={(this.props.employee.client || {}).id}
              component={SelectEmployeeStep}
              editable={true}
            />
            <Step
              title={this.props.t("SELECT_POSITION_TO_ASSIGN_SHIFT_PATTERN_TO")}
              clientId={(this.props.employee.client || {}).id}
              employeeId={(selectedEmployee || {}).id}
              component={SelectPositionToAssignShiftPatternToStep}
              editable={true}
            />
          </LinearWizard>

          {this.props.transferAbsenceConfirmationModal.visible &&
            this.props.selectedEmployee &&
            this.props.absence &&
            this.props.employee && (
              <TransferAbsenceConfirmationModal
                onDismissHandler={this.toggleAbsenceConfirmationModal}
                onSubmitHandler={this.submitTransfer}
                employeeFrom={this.props.employee}
                employeeTo={this.props.selectedEmployee}
                absence={this.props.absence}
              />
            )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.props.onDismissHandler}>
            {this.props.t("CANCEL")}
          </Button>
          <Button onClick={this.submitForm} disabled={!this.isValid()}>
            {this.props.t("TRANSFER")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  selectedEmployee: getSelectedEmployee(state),
  selectedEmployeePosition: getSelectedEmployeePosition(state),
  transferAbsenceConfirmationModal: getTransferAbsenceConfirmationModal(state),
});
const mapDispatchToProps: PropsFromDispatch = {
  submitTransferAbsence: sharedActionCreator.SubmitTransferAbsence,
  selectEmployeePosition: sharedActionCreator.SelectEmployeePosition,
  selectEmployee: sharedActionCreator.SelectEmployee,
  resetSelectedEmployees: sharedActionCreator.ResetSelectedEmployees,
  toggleTransferAbsenceConfirmationModal:
    sharedActionCreator.ToggleTransferAbsenceConfirmationModal,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps, //mapDispatchToProps,
)(localize()(TransferAbsenceModal));
